import { createGraphQLFetchStrategy } from "./graphql.js";
import { fetchGraphQL } from "@universityofmaryland/web-utilities-library/network";
const CALENDARS = ["communications", "submission"];
function generateFragments() {
  return CALENDARS.map(
    (cal) => `
    fragment EventBasicFields_${cal} on ${cal}_Event {
      id
      title
      url
    }

    fragment EventDateFields_${cal} on ${cal}_Event {
      startDayOfWeek: startDate @formatDateTime(format: "D")
      startMonth: startDate @formatDateTime(format: "M")
      startDay: startDate @formatDateTime(format: "d")
      startStamp: startDate @formatDateTime(format: "Y-m-d")
      startTime: startDate @formatDateTime(format: "g:ia")
      endDayOfWeek: endDate @formatDateTime(format: "D")
      endMonth: endDate @formatDateTime(format: "M")
      endDay: endDate @formatDateTime(format: "d")
      endTime: endDate @formatDateTime(format: "g:ia")
      allDay
    }

    fragment EventContentFields_${cal} on ${cal}_Event {
      summary: commonRichTextTwo
      image: commonAssetHeroImageSingle {
        url
        alt
      }
      location: categoriesCampusBuildingSingle {
        title
      }
    }

    fragment EventCategoryFields_${cal} on ${cal}_Event {
      categories: categoriesEventAudienceMultiple {
        id
        title
      }
    }
  `
  ).join("\n");
}
function generateInlineSpreads() {
  return CALENDARS.map(
    (cal) => `
        ... on ${cal}_Event {
          ...EventBasicFields_${cal}
          ...EventDateFields_${cal}
          ...EventContentFields_${cal}
          ...EventCategoryFields_${cal}
        }
  `
  ).join("\n");
}
function generateSliderSpreads() {
  return CALENDARS.map(
    (cal) => `
        ... on ${cal}_Event {
          ...EventSliderFields_${cal}
        }
  `
  ).join("\n");
}
function generateSliderFragments() {
  return CALENDARS.map(
    (cal) => `
    fragment EventSliderFields_${cal} on ${cal}_Event {
      title
      url
      image: commonAssetHeroImageSingle {
        url
        alt
      }
      startMonth: startDate @formatDateTime(format: "M")
      startDay: startDate @formatDateTime(format: "d")
      endMonth: endDate @formatDateTime(format: "M")
      endDay: endDate @formatDateTime(format: "d")
    }
  `
  ).join("\n");
}
const buildEventsCountQuery = (dateFilter = "startsAfterOrAt") => `
  query getEventsCount($startDate: String!, $related: [QueryArgument]) {
    count: solspace_calendar {
      events(relatedTo: $related, loadOccurrences: true, ${dateFilter}: $startDate) {
        ... on communications_Event {
          id
        }
        ... on submission_Event {
          id
        }
      }
    }
  }
`;
const buildEventsQuery = (dateFilter = "startsAfterOrAt") => `
  query getEvents($startDate: String!, $related: [QueryArgument], $limit: Int, $offset: Int) {
    entries: solspace_calendar {
      events(
        relatedTo: $related
        loadOccurrences: true
        ${dateFilter}: $startDate
        limit: $limit
        offset: $offset
      ) {
       ${generateInlineSpreads()}
      }
    }
  }
  ${generateFragments()}
`;
const EVENTS_COUNT_QUERY = buildEventsCountQuery();
const EVENTS_QUERY = buildEventsQuery();
const CATEGORY_NAMES_QUERY = `
  query getCategoryNames($ids: [QueryArgument]!) {
    categories(id: $ids) {
      id
      title
    }
  }
`;
const EVENTS_SLIDER_QUERY = `
  query getEvents($startDate: String!, $related: [QueryArgument]) {
    entries: solspace_calendar {
      events(
        relatedTo: $related
        loadOccurrences: true
        startsAfterOrAt: $startDate
        limit: 12
      ) {
       ${generateSliderSpreads()}
      }
    }
  }
  ${generateSliderFragments()}
`;
const baseFetchStrategy = createGraphQLFetchStrategy({
  endpoint: "https://calendar.umd.edu/graphql",
  queries: {
    entries: EVENTS_QUERY,
    count: EVENTS_COUNT_QUERY
  },
  transformResponse: (data) => {
    if (!data || !data.data || !data.data.entries) {
      return null;
    }
    return data.data.entries.events || null;
  },
  transformCount: (data) => {
    if (!data || !data.data || !data.data.count) {
      return 0;
    }
    return data.data.count.events?.length || 0;
  },
  composeVariables: (baseVariables) => {
    const { categories, ...rest } = baseVariables;
    return {
      ...rest,
      related: categories,
      startDate: (/* @__PURE__ */ new Date()).toDateString()
    };
  }
});
async function fetchCategoryNames(categoryIds, token) {
  try {
    const response = await fetchGraphQL({
      url: "https://calendar.umd.edu/graphql",
      query: CATEGORY_NAMES_QUERY,
      token: token || "",
      variables: { ids: categoryIds }
    });
    if (!response || !response.data || !response.data.categories) {
      return null;
    }
    return response.data.categories.map((category) => category.title);
  } catch (error) {
    console.error("Fetch category names error:", error);
    return null;
  }
}
const eventsFetchStrategy = {
  ...baseFetchStrategy,
  fetchCategoryNames
};
const baseFetchStrategyRange = createGraphQLFetchStrategy({
  endpoint: "https://calendar.umd.edu/graphql",
  queries: {
    entries: buildEventsQuery("rangeStart"),
    count: buildEventsCountQuery("rangeStart")
  },
  transformResponse: (data) => {
    if (!data || !data.data || !data.data.entries) {
      return null;
    }
    return data.data.entries.events || null;
  },
  transformCount: (data) => {
    if (!data || !data.data || !data.data.count) {
      return 0;
    }
    return data.data.count.events?.length || 0;
  },
  composeVariables: (baseVariables) => {
    const { categories, ...rest } = baseVariables;
    return {
      ...rest,
      related: categories,
      startDate: (/* @__PURE__ */ new Date()).toDateString()
    };
  }
});
const eventsFetchStrategyRange = {
  ...baseFetchStrategyRange,
  fetchCategoryNames
};
export {
  CATEGORY_NAMES_QUERY,
  EVENTS_COUNT_QUERY,
  EVENTS_QUERY,
  EVENTS_SLIDER_QUERY,
  eventsFetchStrategy,
  eventsFetchStrategyRange
};
//# sourceMappingURL=events.js.map
